import { useEffect, useRef, useState } from 'react'
import { DataFreshnessCompact } from '../data/DataFreshness'
import { TONE_COLOUR, pillVerdict, useFreshness, type FreshnessSource } from '../data/freshness'
import { shellNavigate } from '../lib/navigation'

// The top-bar data pill (instance.freshnessPill), on every page: "Data up to
// date · 12m ago", coloured by the worst of the core sources. One tap opens
// the per-source list. NZ Legal's WorkspaceFreshnessBadge, minus its Sync now
// button: Command Centre pages show, they do not act (Sam, 16 Oct 2026).

export function FreshnessPill({
  sources,
  variant = 'bar',
  dataPath,
}: {
  sources: FreshnessSource[]
  variant?: 'bar' | 'phone'
  /** Where "See it on the Data page" goes, when this Command Centre has one. */
  dataPath?: string
}) {
  const { rows, loading, now } = useFreshness(sources)
  const verdict = pillVerdict(rows, loading, now)
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const colour = TONE_COLOUR[verdict.tone]
  const phone = variant === 'phone'
  // On a phone the header has room for the dot and the time only.
  const shortLabel = verdict.label.includes('·') ? verdict.label.split('·').pop()!.trim() : verdict.label

  return (
    <div ref={rootRef} className={phone ? 'shrink-0' : 'relative'}>
      <button
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={`${verdict.label}. Show how up to date each source is.`}
        title="How up to date your data is"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-2 rounded-full border text-[12.5px] font-medium transition-colors hover:bg-surface-2"
        style={{
          padding: phone ? '5px 9px' : '5px 12px',
          borderColor: `${colour}55`,
          background: `${colour}12`,
          color: verdict.tone === 'grey' ? undefined : colour,
        }}
      >
        <span className="relative flex h-2.5 w-2.5 items-center justify-center">
          {verdict.tone === 'red' && (
            <span aria-hidden className="absolute inset-0 animate-ping rounded-full opacity-50" style={{ background: colour }} />
          )}
          <i aria-hidden className="relative h-2.5 w-2.5 rounded-full" style={{ background: colour }} />
        </span>
        <span className="whitespace-nowrap">{phone ? shortLabel : verdict.label}</span>
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="How up to date your data is"
          className={`z-50 overflow-hidden rounded-xl border border-border bg-surface-1 shadow-xl ${
            phone ? 'fixed left-2 right-2 top-14' : 'absolute right-0 top-[calc(100%+8px)] w-[400px]'
          }`}
        >
          <div className="border-b border-border px-4 pb-2.5 pt-3">
            <p className="m-0 text-[14px] font-semibold text-text-primary">How up to date your data is</p>
            <p className="m-0 mt-0.5 text-[12px] leading-snug text-text-secondary">
              Every page, and Claude, reads these copies. NZ time.
            </p>
          </div>
          <div className="max-h-[60vh] overflow-y-auto">
            <DataFreshnessCompact rows={rows} now={now} />
          </div>
          {dataPath && (
            <div className="border-t border-border px-4 py-2.5">
              <button
                type="button"
                onClick={() => {
                  setOpen(false)
                  shellNavigate(dataPath)
                }}
                className="text-[12.5px] font-medium text-accent hover:underline"
              >
                See it all on the Data page
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
